import { DateTime } from 'luxon';
import { useState } from 'react';
import { ErrorBox } from './bk';
import { SlotPicker } from './SlotPicker';

interface Props {
  fetchSlots: (fromIso: string, toIso: string) => Promise<string[]>;
  onReschedule: (startIso: string, reason: string, force: boolean) => Promise<void>;
  onCancel: (reason: string) => Promise<void>;
  initialMode?: 'reschedule' | 'cancel';
  timezone: string;
  /** Hosts may pick any time, outside the link's open slots. */
  allowCustomTime?: boolean;
}

/** Reschedule / cancel panel shared by the host meeting page and the invitee manage page. */
export function ChangeMeeting({ fetchSlots, onReschedule, onCancel, initialMode, timezone: tz0, allowCustomTime }: Props) {
  const [mode, setMode] = useState<'reschedule' | 'cancel' | undefined>(initialMode);
  const [tz, setTz] = useState(tz0);
  const [pick, setPick] = useState('');
  const [custom, setCustom] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      setMode(undefined);
      setPick('');
      setReason('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!mode)
    return (
      <div className="row">
        <button className="primary" onClick={() => setMode('reschedule')}>Reschedule</button>
        <button className="danger" onClick={() => setMode('cancel')}>Cancel meeting</button>
      </div>
    );

  return (
    <div className="stack">
      <ErrorBox error={error} />
      {mode === 'reschedule' ? (
        <>
          <h3>Pick a new time</h3>
          <SlotPicker fetchSlots={fetchSlots} timezone={tz} onTimezone={setTz} selected={pick} onPick={(s) => (setPick(s), setCustom(''))} />
          {allowCustomTime && (
            <div className="field">
              <label htmlFor="custom">Or any time (host override)</label>
              <input id="custom" type="datetime-local" value={custom} onChange={(e) => (setCustom(e.target.value), setPick(''))} />
              <div className="hint">Ignores availability rules. Time is in {tz}.</div>
            </div>
          )}
          <div className="field">
            <label htmlFor="rr">Message to attendees (optional)</label>
            <input id="rr" type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="row">
            <button onClick={() => setMode(undefined)}>Back</button>
            <button
              className="primary"
              disabled={busy || (!pick && !custom)}
              onClick={() =>
                run(() =>
                  custom
                    ? onReschedule(DateTime.fromISO(custom, { zone: tz }).toUTC().toISO()!, reason, true)
                    : onReschedule(pick, reason, false),
                )
              }
            >
              {busy ? 'Updating…' : pick || custom ? `Move to ${DateTime.fromISO(pick || DateTime.fromISO(custom, { zone: tz }).toISO()!).setZone(tz).toFormat('ccc LLL d, h:mm a')}` : 'Choose a time'}
            </button>
          </div>
        </>
      ) : (
        <>
          <h3>Cancel this meeting?</h3>
          <p className="muted small">Everyone on the invite gets a cancellation.</p>
          <div className="field">
            <label htmlFor="cr">Reason (shared with attendees)</label>
            <textarea id="cr" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="row">
            <button onClick={() => setMode(undefined)}>Keep meeting</button>
            <button className="danger" disabled={busy} onClick={() => run(() => onCancel(reason))}>{busy ? 'Canceling…' : 'Cancel meeting'}</button>
          </div>
        </>
      )}
    </div>
  );
}
